import type { OptionRating, PollOption } from '@tunasong/schemas'
import { FormControlLabel } from '@tunasong/ui-lib'
import type { FC, ReactNode } from 'react'
import { useState } from 'react'
import { WithOptionAudio } from './option-audio.js'
import { OptionComment, OptionCommentButton } from './option-comment.js'
import { OptionRatingButtons } from './option-rating.js'
import ratingStyles from './option-rating.module.css'
import { PollOptionName } from './poll-option-name.js'
import styles from './poll.module.css'
import VoteButton from './vote-button.js'

export interface VoteOptionRowProps {
  idx: number
  option: PollOption
  picked: boolean
  /** No thumbs, no comment editing: the poll is closed, or these are someone else's answers. */
  readOnly: boolean
  /** The vote button is hidden (read-only, or the quota is reached and this option is not picked). */
  voteDisabled: boolean
  /** Whether to show the voter's comment at all; another member's comments are private. */
  showComment: boolean
  rating?: OptionRating
  comment?: string
  /** Shown after the option name, e.g. the progress bar on a closed poll. */
  result?: ReactNode
  onVote(value: boolean): void
  onRate(rating: OptionRating | undefined): void
  onComment(comment: string): void
}

/**
 * One votable option: vote button, name, audio, and the voter's thumbs and comment. Thumbs and the comment sit
 * outside the <label>, so pressing them never casts a vote. `data-reorder-key` lets the list animate re-sorts.
 */
export const VoteOptionRow: FC<VoteOptionRowProps> = props => {
  const {
    idx,
    option,
    picked,
    readOnly,
    voteDisabled,
    showComment,
    rating,
    comment,
    result,
    onVote,
    onRate,
    onComment,
  } = props
  /** Whether the voter opened the comment field; a saved comment shows regardless. */
  const [isCommentOpen, setCommentOpen] = useState(false)

  return (
    <div
      data-reorder-key={idx}
      className={readOnly ? ratingStyles.ratedRow : `${ratingStyles.ratedRow} ${ratingStyles.interactive}`}
    >
      <WithOptionAudio option={option}>
        <FormControlLabel
          value={idx}
          control={<VoteButton disabled={voteDisabled} status={picked} onChange={(_ev, value) => onVote(value)} />}
          label={
            <div className={styles.optionLabel}>
              <PollOptionName option={option} withAudio={false} />
              {result}
            </div>
          }
          className={styles.formLabel}
        />
      </WithOptionAudio>
      {readOnly ? null : (
        <OptionRatingButtons option={option} rating={rating} onChange={onRate}>
          <OptionCommentButton isOpen={isCommentOpen || Boolean(comment)} onOpen={() => setCommentOpen(true)} />
        </OptionRatingButtons>
      )}
      {showComment ? (
        <OptionComment
          option={option}
          comment={comment}
          readOnly={readOnly}
          isOpen={isCommentOpen}
          onClose={() => setCommentOpen(false)}
          onChange={onComment}
        />
      ) : null}
    </div>
  )
}

export default VoteOptionRow
